import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTitle, Meta, Display, Chip, Icons } from '../components/Atoms';
import Layout, { useViewport, TopBar } from '../components/Layout';
import { LoadingState, ErrorState, EmptyState } from '../components/states';
import { useMultipleApi, useApi, daysSince } from '../lib/api';
import { formatLocalDate } from '../i18n';

/**
 * Planner - plan sezonu z prawdziwymi danymi:
 *
 * 3 grupy z perspektywy ogrodnika:
 *   1. Wysiewy / sadzenia - z bedów których planted_date jest w przyszłości
 *   2. Sukcesja (auto) - z /api/succession aktywne
 *   3. Plan {rok+1} (rotacja) - placeholder na razie (wymaga osobnej logiki)
 *
 * Plus oś sezonu (7 miesięcy IV-X) z licznikiem wpisów per miesiąc.
 *
 * Endpoints:
 *   /api/plots/all-with-details - beds (planted_date przyszłość = wysiew czeka)
 *   /api/succession - cykliczne wysiewy z next_planting_date
 *   /api/tasks - taski cykliczne i przyszłe
 */

const SEASON_MONTHS = [
  { idx: 3, m: 'IV',  name: 'kwiecień'   },
  { idx: 4, m: 'V',   name: 'maj'        },
  { idx: 5, m: 'VI',  name: 'czerwiec'   },
  { idx: 6, m: 'VII', name: 'lipiec'     },
  { idx: 7, m: 'VIII',name: 'sierpień'   },
  { idx: 8, m: 'IX',  name: 'wrzesień'   },
  { idx: 9, m: 'X',   name: 'październik' },
];

// ── Helpers ──────────────────────────────────────────────────────
function bedToWysiewItem(bed, plotName) {
  if (!bed.planted_date) return null;
  const days = -daysSince(bed.planted_date);
  if (days < 0) return null; // już posadzone
  return {
    date: bed.planted_date,
    title: `${bed.plant_name || 'Roślina'}${bed.plant_variety ? ` (${bed.plant_variety})` : ''}`,
    plot: plotName,
    daysOut: days,
    note: bed.note,
  };
}

function successionToItem(s) {
  return {
    date: s.next_planting_date,
    title: `${s.plant_name} — kolejny wysiew`,
    plot: s.bed_id ? `grządka #${s.bed_id}` : 'sukcesja',
    every: `co ${s.interval_days} dni`,
  };
}

function countItemsPerMonth(items) {
  const counts = {};
  items.forEach(item => {
    if (!item.date) return;
    const m = new Date(item.date).getMonth();
    counts[m] = (counts[m] || 0) + 1;
  });
  return counts;
}

// ── Pasek miesiąca ──────────────────────────────────────────────
function MonthBar({ m, isCurrent, count }) {
  return (
    <div style={{
      flex: 1, padding: '14px 0 16px',
      borderRadius: 10,
      background: isCurrent ? 'var(--t-sage)' : 'var(--t-bg-elev)',
      color: isCurrent ? 'var(--t-bg)' : 'var(--t-text)',
      border: `1px solid ${isCurrent ? 'var(--t-sage)' : 'var(--t-line)'}`,
      textAlign: 'center', cursor: 'pointer',
      minWidth: 64,
    }}>
      <Display size={16} style={{
        color: 'inherit', display: 'block',
        fontFamily: 'var(--t-mono)', letterSpacing: '.05em',
      }}>{m.m}</Display>
      <div style={{
        fontSize: 10, fontFamily: 'var(--t-mono)',
        letterSpacing: '.14em', opacity: 0.7, marginTop: 4,
        textTransform: 'uppercase',
      }}>{m.name}</div>
      {count > 0 && (
        <div style={{
          marginTop: 8, fontSize: 10,
          fontFamily: 'var(--t-mono)', opacity: 0.65,
        }}>{count} {count === 1 ? 'wpis' : count < 5 ? 'wpisy' : 'wpisów'}</div>
      )}
    </div>
  );
}

// ── Plan item row ──────────────────────────────────────────────
function PlanItem({ item, color }) {
  return (
    <div style={{
      display: 'flex', gap: 14, alignItems: 'center',
      padding: '14px 0',
      borderBottom: '1px solid var(--t-line)',
    }}>
      <div style={{
        width: 80, flexShrink: 0,
        textAlign: 'right',
      }}>
        <Display size={14} style={{ color, lineHeight: 1.1, display: 'block' }}>
          {item.date ? formatLocalDate(item.date, 'short') : '—'}
        </Display>
        {item.daysOut > 0 && (
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 10,
            letterSpacing: '.12em', color: 'var(--t-text-mute)',
            marginTop: 2,
          }}>za {item.daysOut} dni</div>
        )}
      </div>
      <div style={{ width: 1, height: 32, background: 'var(--t-line-2)' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <Display size={16} style={{ display: 'block', lineHeight: 1.2 }}>
          {item.title}
        </Display>
        <div style={{
          fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3,
          display: 'flex', gap: 12,
        }}>
          {item.plot && <span>{item.plot}</span>}
          {item.every && <span>· {item.every}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Group section ───────────────────────────────────────────────
function GroupSection({ title, color, items, emptyText }) {
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        paddingBottom: 10, marginBottom: 4,
        borderBottom: `1px solid ${color}40`,
      }}>
        <div style={{ width: 4, height: 18, background: color, borderRadius: 2 }} />
        <Display size={20} style={{ color }}>{title}</Display>
        <span style={{
          marginLeft: 'auto',
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.14em', color: 'var(--t-text-mute)',
        }}>{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div style={{
          padding: '16px 0', fontSize: 13,
          color: 'var(--t-text-mute)', fontStyle: 'italic',
        }}>↳ {emptyText}</div>
      ) : items.slice(0, 12).map((item, i) => (
        <PlanItem key={i} item={item} color={color} />
      ))}
    </div>
  );
}

// ── Main content ────────────────────────────────────────────────
function MainContent({ plots, succession, gardenPlans }) {
  const { t } = useTranslation();

  // Grupa 1: Wysiewy/sadzenia czekające (planted_date w przyszłości)
  const wysiewy = useMemo(() => {
    const items = [];
    if (Array.isArray(plots)) {
      plots.forEach(plot => {
        (plot.beds || []).forEach(bed => {
          const item = bedToWysiewItem(bed, plot.name);
          if (item) items.push(item);
        });
      });
    }
    items.sort((a, b) => new Date(a.date) - new Date(b.date));
    return items;
  }, [plots]);

  // Grupa 2: Sukcesja aktywna
  const sukcesja = useMemo(() => {
    if (!Array.isArray(succession)) return [];
    return succession
      .filter(s => s.is_active)
      .map(successionToItem)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [succession]);

  // Grupa 3: Plany sezonowe (z /api/garden-plans)
  const plany = useMemo(() => {
    if (!Array.isArray(gardenPlans)) return [];
    return gardenPlans.map(plan => ({
      date: plan.planned_planting_date,
      title: `${plan.name} — ${plan.items_count || 0} pozycji`,
      plot: plan.plot_name || `plan ${plan.year}`,
      every: plan.status === 'draft' ? 'wersja robocza' : null,
    }));
  }, [gardenPlans]);

  const allItems = [...wysiewy, ...sukcesja, ...plany];
  const monthCounts = useMemo(() => countItemsPerMonth(allItems), [allItems]);
  const currentMonth = new Date().getMonth();

  return (
    <div className="t-stagger">
      <Meta style={{ marginBottom: 14 }}>Oś sezonu</Meta>
      <div style={{
        display: 'flex', gap: 8,
        marginBottom: 32, overflowX: 'auto', paddingBottom: 4,
      }}>
        {SEASON_MONTHS.map(m => (
          <MonthBar
            key={m.m}
            m={m}
            isCurrent={m.idx === currentMonth}
            count={monthCounts[m.idx] || 0}
          />
        ))}
      </div>

      <GroupSection
        title="Wysiewy / sadzenia"
        color="var(--t-sage)"
        items={wysiewy}
        emptyText="Wszystko już posadzone — czas pielęgnować."
      />

      <GroupSection
        title="Sukcesja (auto)"
        color="var(--t-amber)"
        items={sukcesja}
        emptyText="Brak cyklicznych wysiewów — dodaj sukcesję rzodkiewki czy sałaty żeby mieć je przez cały sezon."
      />

      {plany.length > 0 && (
        <GroupSection
          title={`Plany sezonowe ${new Date().getFullYear()}`}
          color="var(--t-rust)"
          items={plany}
          emptyText="Brak planów — stwórz pierwszy w GardenPlanner."
        />
      )}

      {/* Sezonowa wskazówka */}
      <div style={{
        padding: '16px 20px', borderRadius: 12,
        background: 'rgba(220, 180, 100, 0.08)',
        borderLeft: '3px solid var(--t-amber)',
      }}>
        <Meta color="amber" style={{ marginBottom: 6 }}>Wskazówka sezonowa</Meta>
        <Display size={16} style={{
          display: 'block', lineHeight: 1.35, textWrap: 'balance',
        }}>
          15-25 maja to ostatnie przymrozki w strefach 6b-7a. Pomidor, papryka,
          ogórek i cukinia idą do gruntu po 15.05.
        </Display>
      </div>
    </div>
  );
}

// ── Mobile / Desktop ────────────────────────────────────────────
function MobileContent(props) {
  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '24px 24px 0',
      }}>
        <Meta>Sezon {new Date().getFullYear()}</Meta>
      </div>
      <PageTitle title="Plan sezonu" eyebrow="Co i kiedy" />
      <div style={{ padding: '0 24px' }}>
        <MainContent {...props} />
      </div>
    </div>
  );
}

function DesktopContent(props) {
  return (
    <div style={{ padding: '32px 40px 60px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 18,
        marginBottom: 28, paddingBottom: 18,
        borderBottom: '1px solid var(--t-line)',
      }}>
        <div>
          <Meta style={{ marginBottom: 6 }}>Sezon {new Date().getFullYear()} · plan</Meta>
          <Display size={48} style={{
            display: 'block', letterSpacing: '-.03em',
          }}>
            Co i <span style={{ color: 'var(--t-sage)', fontStyle: 'italic' }}>kiedy</span>
          </Display>
        </div>
        <div style={{ flex: 1 }} />
        <Link to="/calendar" style={{ textDecoration: 'none' }}>
          <Meta color="sage">→ Kalendarz</Meta>
        </Link>
      </div>
      <MainContent {...props} />
    </div>
  );
}

// ── WhatToPlant page (T5) — pełna lista ─────────────────────────
function WhatToPlantPage() {
  const { data, loading, error } = useApi('/api/planner/what-to-plant');

  if (error) {
    return (
      <Layout active="cal">
        <EmptyState title="Nie udało się pobrać" description="Spróbuj ponownie później." />
      </Layout>
    );
  }
  if (loading) {
    return <Layout active="cal"><LoadingState /></Layout>;
  }
  if (!data || !data.groups || data.groups.length === 0) {
    return (
      <Layout active="cal">
        <EmptyState
          title="Brak propozycji na ten miesiąc"
          description={`W ${data?.month || 'bieżącym'} miesiącu nie ma roślin z kalendarza siewu. Sprawdź w innym miesiącu.`}
        />
      </Layout>
    );
  }

  const monthNames = ['', 'styczeń','luty','marzec','kwiecień','maj','czerwiec','lipiec','sierpień','wrzesień','październik','listopad','grudzień'];
  const monthName = monthNames[data.month] || '';

  const Content = ({ isPhone }) => (
    <div style={isPhone ? { padding: '0 24px' } : {}}>
      <Meta color="sage" style={{ marginBottom: 14 }}>
        {data.month}. {monthName} · {data.lastFrostPassed ? 'po przymrozkach' : '⚠ przed przymrozkami'}
      </Meta>
      <Display size={'clamp(36px, 6vw, 48px)'} style={{
        display: 'block', letterSpacing: '-.03em', lineHeight: 1,
        marginBottom: 14,
      }}>Co posadzić w {monthName}?</Display>
      <div style={{
        fontSize: 15, color: 'var(--t-text-dim)', lineHeight: 1.55,
        marginBottom: 32, maxWidth: 540,
      }}>
        {data.totalPlants} propozycji na podstawie kalendarza siewu.
        {!data.lastFrostPassed && ' Rośliny wrażliwe na mróz oznaczono jako „wkrótce".'}
      </div>

      {data.groups.map(group => (
        <div key={group.method} style={{ marginBottom: 28 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 10, paddingBottom: 8,
            borderBottom: '1px solid var(--t-line)',
          }}>
            <Meta>{group.methodIcon} {group.methodLabel}</Meta>
            <span style={{
              fontFamily: 'var(--t-mono)', fontSize: 10,
              color: 'var(--t-text-mute)', marginLeft: 'auto',
            }}>{group.items.length}</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {group.items.map((item, i) => (
              <div key={i} style={{
                padding: '8px 12px', borderRadius: 8,
                background: item.status === 'soon'
                  ? 'rgba(220, 180, 100, 0.06)'
                  : 'var(--t-bg-elev)',
                border: `1px solid ${
                  item.status === 'soon' ? 'rgba(220, 180, 100, 0.30)' : 'var(--t-line)'
                }`,
                borderLeft: `3px solid ${
                  item.status === 'soon' ? 'var(--t-amber)' : 'var(--t-sage)'
                }`,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 14, color: 'var(--t-text)' }}>
                    {item.plant}
                  </span>
                  {item.note && (
                    <span style={{
                      fontSize: 11, color: 'var(--t-text-dim)', marginLeft: 6,
                      fontStyle: 'italic',
                    }}>— {item.note}</span>
                  )}
                </div>
                {item.status === 'soon' && (
                  <span style={{
                    fontFamily: 'var(--t-mono)', fontSize: 10,
                    letterSpacing: '.14em', textTransform: 'uppercase',
                    color: 'var(--t-amber)',
                    whiteSpace: 'nowrap',
                  }}>wkrótce</span>
                )}
                {item.window && (
                  <span style={{
                    fontFamily: 'var(--t-mono)', fontSize: 10,
                    letterSpacing: '.1em', color: 'var(--t-text-mute)',
                    whiteSpace: 'nowrap',
                  }}>{item.window.from}-{item.window.to}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Layout active="cal" topBar={<TopBar left={<Meta>Co posadzić</Meta>} />}>
      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 0 60px' }}>
        <ViewportSwitch>
          <WtpMobile><Content isPhone={true} /></WtpMobile>
          <WtpDesktop><Content isPhone={false} /></WtpDesktop>
        </ViewportSwitch>
      </div>
    </Layout>
  );
}

// Prosty ViewportSwitch (kopiowany z Today)
function ViewportSwitch({ children }) {
  const { isPhone } = useViewport();
  const [mobile, desktop] = React.Children.toArray(children);
  return isPhone ? mobile : desktop;
}
function WtpMobile({ children }) { return children; }
function WtpDesktop({ children }) { return children; }

export { WhatToPlantPage };

// ── Planner screen root ──────────────────────────────────────────
export default function Planner() {
  const { t } = useTranslation();
  const { isPhone } = useViewport();

  const { results, loading, errors } = useMultipleApi({
    plots: '/api/plots/all-with-details',
    succession: '/api/succession',
    plans: '/api/garden-plans',
  });

  const firstError = errors.plots || errors.succession || errors.plans;
  if (firstError && (firstError.includes('autoryz') || firstError === 'Brak tokenu autoryzacji')) {
    return <Layout active="cal"><ErrorState error={{ status: 401, message: firstError }} /></Layout>;
  }
  if (loading) return <Layout active="cal"><LoadingState label={t('common.loading')} /></Layout>;

  const plots = results.plots || [];
  const succession = results.succession || [];
  const gardenPlans = results.plans || [];

  if (isPhone) return (
    <Layout active="cal">
      <MobileContent plots={plots} succession={succession} gardenPlans={gardenPlans} />
    </Layout>
  );

  return (
    <Layout active="cal" topBar={<TopBar
      left={<Meta>Plan sezonu {new Date().getFullYear()}</Meta>}
      right={<Meta color="sage">+ Nowy plan</Meta>}
    />}>
      <DesktopContent plots={plots} succession={succession} gardenPlans={gardenPlans} />
    </Layout>
  );
}
